// ─── Rune Words ───────────────────────────────────────────────────────────────
// A Rune Word activates when ALL required runes are simultaneously socketed.
// Bonuses stack on top of individual rune bonuses.
// `requiredRunes` uses exact rune `name` strings for matching.
//
// Scaling guide:
//   3 runes (all common)      → modest bonus
//   4 runes (mixed rarities)  → solid bonus
//   5 runes (up to rare)      → strong bonus
//   6 runes (up to epic)      → very strong bonus
//   7 runes (up to legendary) → powerful bonus
//   8 runes (all legendary)   → godlike bonus

export const RUNE_WORDS = [

  // ════════════════════════════════════════════════════════════════
  //  3-RUNE WORDS  (common combinations — good starter goals)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'iron_bulwark',
    name: 'Iron Bulwark',
    emoji: '🛡️',
    requiredRunes: ['Rune of Endurance', 'Rune of Protection', 'Rune of Warding'],
    statMods: { armor: 4, cdMult: -0.10 },
    description: 'A wall of enduring stone. Your body becomes a fortress.',
  },
  {
    id: 'bladestorm',
    name: 'Bladestorm',
    emoji: '⚔️',
    requiredRunes: ['Rune of Striking', 'Rune of Aggression', 'Rune of Swiftness'],
    statMods: { attackDamage: 1.5, speedMult: 0.10 },
    description: 'Every movement becomes an act of violence.',
  },
  {
    id: 'crimson_edge',
    name: 'Crimson Edge',
    emoji: '🩸',
    requiredRunes: ['Rune of Aggression', 'Rune of Focus', 'Rune of Precision'],
    statMods: { attackDamage: 1.0, critRate: 0.08 },
    description: 'Focus your aggression into a razor-sharp killing edge.',
  },
  {
    id: 'windstrider',
    name: 'Windstrider',
    emoji: '💨',
    requiredRunes: ['Rune of Speed', 'Rune of Swiftness', 'Rune of Alacrity'],
    statMods: { speedMult: 0.12, cdMult: -0.08 },
    description: 'Become the wind itself — swift, ceaseless, unstoppable.',
  },

  // ════════════════════════════════════════════════════════════════
  //  4-RUNE WORDS  (mixed rarities — meaningful progression)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'stormfront',
    name: 'Stormfront',
    emoji: '⛈️',
    requiredRunes: ['Rune of Velocity', 'Rune of Reach', 'Rune of Storm', 'Rune of Expansion'],
    statMods: { speedMult: 0.20, rangeMult: 0.20, lightningRes: 0.15 },
    description: 'Command the storm: reach farther, move faster, resist the thunder.',
  },
  {
    id: 'shadow_arts',
    name: 'Shadow Arts',
    emoji: '🌑',
    requiredRunes: ['Rune of Dispatch', 'Rune of Cunning', 'Rune of Precision', 'Rune of Deadliness'],
    statMods: { cdMult: -0.12, critDamage: 0.15, critRate: 0.08 },
    description: 'Strike from the shadows. Death comes swift and silent.',
  },
  {
    id: 'stone_sentinel',
    name: 'Stone Sentinel',
    emoji: '🗿',
    requiredRunes: ['Rune of Protection', 'Rune of Warding', 'Rune of Resilience', 'Rune of Fortitude'],
    statMods: { armor: 12, fireRes: 0.10 },
    description: 'An ancient guardian awakens within. Nothing can break you.',
  },
  {
    id: 'arcane_mind',
    name: 'Arcane Mind',
    emoji: '🔬',
    requiredRunes: ['Rune of Focus', 'Rune of Insight', 'Rune of Reach', 'Rune of Erudition'],
    statMods: { xpPerKill: 3, critRate: 0.05, rangeMult: 0.15 },
    description: 'Knowledge is power. Channel it into devastating precision.',
  },

  // ════════════════════════════════════════════════════════════════
  //  5-RUNE WORDS  (rare+ tier — endgame mid power)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'warlord',
    name: 'Warlord',
    emoji: '🗡️',
    requiredRunes: ['Rune of Impact', 'Rune of Brutality', 'Rune of Dominance', 'Rune of Mastery', 'Rune of Carnage'],
    statMods: { attackDamage: 3.0, critRate: 0.10 },
    description: 'Five runes, one purpose: total annihilation of your enemies.',
  },
  {
    id: 'phantom_wind',
    name: 'Phantom Wind',
    emoji: '🌪️',
    requiredRunes: ['Rune of Speed', 'Rune of Velocity', 'Rune of Momentum', 'Rune of Haste', 'Rune of Celerity'],
    statMods: { speedMult: 0.35, cdMult: -0.15 },
    description: 'You are no longer a person — you are a force of nature.',
  },
  {
    id: 'firestorm',
    name: 'Firestorm',
    emoji: '🔥',
    requiredRunes: ['Rune of Flame', 'Rune of Storm', 'Rune of Expansion', 'Rune of Extension', 'Rune of Conflagration'],
    statMods: { fireRes: 0.30, lightningRes: 0.20, rangeMult: 0.25 },
    description: 'Flames and lightning merge into a consuming elemental fury.',
  },

  // ════════════════════════════════════════════════════════════════
  //  6-RUNE WORDS  (epic tier — high-investment power spikes)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'deaths_herald',
    name: "Death's Herald",
    emoji: '💀',
    requiredRunes: ['Rune of Cunning', 'Rune of Deadliness', 'Rune of Decimation', 'Rune of Devastation', 'Rune of Slaughter', 'Rune of Annihilation'],
    statMods: { critDamage: 0.40, critRate: 0.15, attackDamage: 1.0 },
    description: 'The herald of the end arrives. Every blow is a killing blow.',
  },
  {
    id: 'unbreakable',
    name: 'Unbreakable',
    emoji: '🏔️',
    requiredRunes: ['Rune of Endurance', 'Rune of Protection', 'Rune of Resilience', 'Rune of Fortitude', 'Rune of Bastion', 'Rune of Invulnerability'],
    statMods: { armor: 18, fireRes: 0.10, lightningRes: 0.10 },
    description: 'Six runestones forge an indestructible body of living iron.',
  },
  {
    id: 'conquerors_path',
    name: "Conqueror's Path",
    emoji: '👑',
    requiredRunes: ['Rune of Mastery', 'Rune of Carnage', 'Rune of Slaughter', 'Rune of Obliteration', 'Rune of the Conqueror', 'Rune of Dominance'],
    statMods: { attackDamage: 5.0, critRate: 0.15 },
    description: 'Walk the path of conquest. Leave nothing standing in your wake.',
  },

  // ════════════════════════════════════════════════════════════════
  //  7-RUNE WORDS  (legendary tier — near-complete build payoffs)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'ascendant',
    name: 'Ascendant',
    emoji: '✨',
    requiredRunes: ['Rune of Striking', 'Rune of Aggression', 'Rune of Impact', 'Rune of Dominance', 'Rune of Mastery', 'Rune of Carnage', 'Rune of Obliteration'],
    statMods: { attackDamage: 4.0, speedMult: 0.10, critRate: 0.10 },
    description: 'From humble origins to absolute power. Your ascension is complete.',
  },
  {
    id: 'gale_force',
    name: 'Gale Force',
    emoji: '🌀',
    requiredRunes: ['Rune of Speed', 'Rune of Velocity', 'Rune of Momentum', 'Rune of Celerity', 'Rune of Transcendence', 'Rune of Haste', 'Rune of Quickening'],
    statMods: { speedMult: 0.45, cdMult: -0.25 },
    description: 'Seven runes aligned. You slip between moments themselves.',
  },
  {
    id: 'reapers_covenant',
    name: "Reaper's Covenant",
    emoji: '☠️',
    requiredRunes: ['Rune of Precision', 'Rune of Cunning', 'Rune of Deadliness', 'Rune of Decimation', 'Rune of Devastation', 'Rune of Execution', 'Rune of Lethal Edge'],
    statMods: { critRate: 0.20, critDamage: 0.50 },
    description: 'A covenant with death. Every strike guaranteed to be lethal.',
  },

  // ════════════════════════════════════════════════════════════════
  //  8-RUNE WORDS  (all-legendary — godlike, extremely rare)
  // ════════════════════════════════════════════════════════════════

  {
    id: 'absolute_dominion',
    name: 'Absolute Dominion',
    emoji: '💥',
    requiredRunes: ['Rune of Aggression', 'Rune of Brutality', 'Rune of Dominance', 'Rune of Carnage', 'Rune of Slaughter', 'Rune of Obliteration', 'Rune of the Conqueror', 'Rune of the Void'],
    statMods: { attackDamage: 8.0, critRate: 0.20, speedMult: 0.20 },
    description: 'Total dominion over life and death. None can stand against you.',
  },
  {
    id: 'eternitys_gate',
    name: "Eternity's Gate",
    emoji: '🌌',
    requiredRunes: ['Rune of Warding', 'Rune of Fortitude', 'Rune of Invulnerability', 'Rune of Transcendence', 'Rune of Omnipotence', 'Rune of Eternity', 'Rune of Enlightenment', 'Rune of Rapidity'],
    statMods: { armor: 15, critRate: 0.25, cdMult: -0.30, xpPerKill: 3 },
    description: 'Stand at the threshold of eternity. Time, flesh, and fate are yours.',
  },
  {
    id: 'apex_predator',
    name: 'Apex Predator',
    emoji: '🦁',
    requiredRunes: ['Rune of Execution', 'Rune of Obliteration', 'Rune of the Conqueror', 'Rune of the Void', 'Rune of Transcendence', 'Rune of Lethal Edge', 'Rune of Rapidity', 'Rune of Annihilation'],
    statMods: { attackDamage: 6.0, critRate: 0.30, critDamage: 0.50 },
    description: 'The ultimate predator. Nothing survives. Nothing escapes.',
  },

  // ════════════════════════════════════════════════════════════════
  //  NEW HYBRID WORDS  (offensive + defensive + magic find)
  // ════════════════════════════════════════════════════════════════

  // 3-rune hybrid
  {
    id: 'fortunes_blade',
    name: "Fortune's Blade",
    emoji: '💰',
    requiredRunes: ['Rune of Striking', 'Rune of Insight', 'Rune of Reach'],
    statMods: { attackDamage: 1.0, magicFind: 0.10, goldFindBonus: 0.10 },
    description: 'The sharp edge that cuts fate itself — every kill enriches you.',
  },
  {
    id: 'iron_scholar',
    name: 'Iron Scholar',
    emoji: '📚',
    requiredRunes: ['Rune of Protection', 'Rune of Insight', 'Rune of Warding'],
    statMods: { armor: 5, xpPerKill: 2, magicFind: 0.08 },
    description: 'Wisdom and iron fused. Knowledge protects as much as steel.',
  },

  // 4-rune hybrid
  {
    id: 'seekers_bulwark',
    name: "Seeker's Bulwark",
    emoji: '🔍',
    requiredRunes: ['Rune of Protection', 'Rune of Resilience', 'Rune of Insight', 'Rune of Erudition'],
    statMods: { armor: 8, magicFind: 0.15, xpPerKill: 2 },
    description: 'A fortified mind hunts fortune through the chaos of battle.',
  },
  {
    id: 'battle_scholar',
    name: 'Battle Scholar',
    emoji: '⚔️',
    requiredRunes: ['Rune of Power', 'Rune of Focus', 'Rune of Insight', 'Rune of Dominance'],
    statMods: { attackDamage: 1.5, armor: 4, xpPerKill: 2, magicFind: 0.10 },
    description: 'Study the art of war. Knowledge and fury serve you equally.',
  },

  // 5-rune hybrid
  {
    id: 'golden_path',
    name: 'Golden Path',
    emoji: '✨',
    requiredRunes: ['Rune of Insight', 'Rune of Cunning', 'Rune of Erudition', 'Rune of Enlightenment', 'Rune of Expansion'],
    statMods: { magicFind: 0.30, goldFindBonus: 0.25, armor: 6, xpPerKill: 2 },
    description: 'The path of the fortunate — where you walk, treasures emerge.',
  },
  {
    id: 'crimson_harvest',
    name: 'Crimson Harvest',
    emoji: '🌾',
    requiredRunes: ['Rune of Carnage', 'Rune of Brutality', 'Rune of Mastery', 'Rune of Insight', 'Rune of Erudition'],
    statMods: { attackDamage: 2.5, critRate: 0.08, magicFind: 0.20, goldFindBonus: 0.15 },
    description: 'Reap what you sow in blood. Every enemy is a treasure chest.',
  },

  // 6-rune hybrid
  {
    id: 'gilded_fortress',
    name: 'Gilded Fortress',
    emoji: '🏰',
    requiredRunes: ['Rune of Fortitude', 'Rune of Bastion', 'Rune of Invulnerability', 'Rune of Erudition', 'Rune of Enlightenment', 'Rune of Expansion'],
    statMods: { armor: 14, fireRes: 0.15, magicFind: 0.20, goldFindBonus: 0.20 },
    description: 'A fortress of gold and iron — impenetrable and overflowing with wealth.',
  },
  {
    id: 'void_seeker',
    name: 'Void Seeker',
    emoji: '🌌',
    requiredRunes: ['Rune of Carnage', 'Rune of Slaughter', 'Rune of Devastation', 'Rune of Enlightenment', 'Rune of Erudition', 'Rune of Insight'],
    statMods: { attackDamage: 3.0, critDamage: 0.20, magicFind: 0.25, xpPerKill: 3 },
    description: 'Hunt through the void for power and treasure beyond reckoning.',
  },

  // 7-rune hybrid
  {
    id: 'seeker_supreme',
    name: 'Seeker Supreme',
    emoji: '👁️',
    requiredRunes: ['Rune of Obliteration', 'Rune of Carnage', 'Rune of Invulnerability', 'Rune of Bastion', 'Rune of Enlightenment', 'Rune of Erudition', 'Rune of Transcendence'],
    statMods: { attackDamage: 3.0, armor: 10, magicFind: 0.35, goldFindBonus: 0.25 },
    description: 'See all. Find all. Destroy all. Supreme in power and fortune alike.',
  },

  // 8-rune hybrid
  {
    id: 'eternitys_harvest',
    name: "Eternity's Harvest",
    emoji: '🌟',
    requiredRunes: ['Rune of Lethal Edge', 'Rune of the Conqueror', 'Rune of Invulnerability', 'Rune of Omnipotence', 'Rune of Eternity', 'Rune of Enlightenment', 'Rune of Transcendence', 'Rune of Erudition'],
    statMods: { attackDamage: 4.0, armor: 12, magicFind: 0.50, goldFindBonus: 0.35, xpPerKill: 4 },
    description: 'Harvest the riches of eternity itself. Power, wealth, and wisdom without limit.',
  },
];

/**
 * Returns the subset of RUNE_WORDS whose required runes are all
 * currently socketed in the equipped map.
 */
export function getActiveRuneWords(equipped) {
  const equippedNames = new Set(
    Object.values(equipped)
      .filter(item => item?.slotType === 'rune')
      .map(item => item.name)
  );
  return RUNE_WORDS.filter(word =>
    word.requiredRunes.every(name => equippedNames.has(name))
  );
}
